import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../supabase/client';
import { apiFetch } from '../apiFetch';

export interface AuthContextType {
  session: Session | null;
  supabaseUser: SupabaseUser | null;
  isSignedIn: boolean;
  isLoaded: boolean;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: string | null }>;
  signUp: (
    email: string,
    password: string,
    metadata?: { first_name?: string; last_name?: string; role?: string }
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setIsLoaded(true);
    });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'SIGNED_OUT') {
        // Session expired or was revoked — try to log it
        try {
          apiFetch('/api/auth/session-ended', {
            method: 'POST',
            body: JSON.stringify({ timed_out: true }),
          });
        } catch {
          /* non-critical */
        }
      }
      setSession(s);
      setIsLoaded(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (!error) {
      // Log session start and kill other sessions
      try {
        await apiFetch('/api/auth/session-started', { method: 'POST' });
      } catch {
        /* non-critical */
      }
      try {
        await supabase.auth.signOut({ scope: 'others' });
      } catch {
        /* non-critical */
      }
    }
    return { error: error?.message ?? null };
  };

  const signUp = async (
    email: string,
    password: string,
    metadata?: { first_name?: string; last_name?: string; role?: string }
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    try {
      await apiFetch('/api/auth/session-ended', { method: 'POST' });
    } catch {
      /* non-critical */
    }
    await supabase.auth.signOut();
  };

  const getToken = async (): Promise<string | null> => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        supabaseUser: session?.user ?? null,
        isSignedIn: !!session,
        isLoaded,
        signIn,
        signUp,
        signOut,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useSupabaseAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useSupabaseAuth must be used within an AuthProvider');
  }
  return context;
};
